import React, { useEffect } from 'react';
import { X, Info, Clock, Truck, PackageOpen, Ruler, ArrowRightLeft } from 'lucide-react';
import {
  SERVICES,
  EASY_RETURN_45,
  DEADLINE_EXPLAINER,
  DELIVERY_TYPES,
  PICKUP_TYPES,
  PARCEL_SIZES,
} from '../lib/hpServices';

/**
 * Reference dialog explaining what the HP service codes mean.
 *
 * Opened from the service pickers in Settings and CreateLabelModal, because
 * "EasyReturn D+3" and "Paket24 D+2" tell a merchant nothing on their own and
 * HP publishes no endpoint we could look them up from. Everything rendered
 * here is static content from src/lib/hpServices.js.
 *
 * `highlight` optionally scopes the service list to one direction, so opening
 * it from the return picker does not lead with outbound-only tiers.
 */
export default function ServiceGuideModal({ isOpen, onClose, highlight = null }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const services = highlight
    ? SERVICES.filter((service) => service.directions.includes(highlight))
    : SERVICES;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container"
        style={{ maxWidth: '760px' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-guide-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                background: 'var(--badge-blue-bg)',
                color: 'var(--badge-blue-text)',
                padding: '0.4rem',
                borderRadius: '50%',
                display: 'flex',
              }}
            >
              <Info size={20} />
            </div>
            <div>
              <h3 id="service-guide-title" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                HP services explained
              </h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-secondary)' }}>
                What each service code means and when to pick it
              </p>
            </div>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            style={{ padding: '0.4rem' }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <Section icon={Clock} title={DEADLINE_EXPLAINER.title}>
            <p style={paragraph}>{DEADLINE_EXPLAINER.body}</p>
            <ul style={bulletList}>
              {DEADLINE_EXPLAINER.bullets.map((line) => (
                <li key={line} style={bulletItem}>
                  {line}
                </li>
              ))}
            </ul>
          </Section>

          <Section icon={Truck} title="Service codes">
            {services.map((service) => (
              <article key={service.code} style={serviceCard}>
                <header style={serviceHeader}>
                  <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{service.name}</span>
                  <span className="badge badge-gray" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    code {service.code}
                  </span>
                  {service.recommendedFor && (
                    <span className="badge badge-green">
                      Default for {service.recommendedFor === 'OUTBOUND' ? 'shipping' : 'returns'}
                    </span>
                  )}
                  {service.directions.length === 1 && (
                    <span className="badge badge-purple">
                      {service.directions[0] === 'RETURN' ? 'Returns only' : 'Shipping only'}
                    </span>
                  )}
                </header>

                <p style={{ ...paragraph, fontWeight: 500, color: 'var(--color-primary)' }}>
                  {service.summary}
                </p>
                <p style={paragraph}>{service.detail}</p>

                {service.bestFor && (
                  <p style={paragraph}>
                    <strong style={{ color: 'var(--color-primary)' }}>Best for:</strong> {service.bestFor}
                  </p>
                )}

                {service.rules && (
                  <ul style={bulletList}>
                    {service.rules.map((rule) => (
                      <li key={rule} style={bulletItem}>
                        {rule}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </Section>

          <Section icon={ArrowRightLeft} title="Why EasyReturn is different">
            <p style={paragraph}>
              EasyReturn is not a faster or slower Paket24 — it is a return that HP bills to you rather
              than to the customer, so the customer sends the parcel back at no cost to them. HP enforces
              that: the payer must be the recipient of the return (your store), on a contracted account,
              and both ends must be registered businesses rather than private individuals. The three
              variants differ only in when the return label is created.
            </p>
            <article style={{ ...serviceCard, background: 'var(--color-bg-subtle)' }}>
              <header style={serviceHeader}>
                <span style={{ fontWeight: 600, fontSize: '0.92rem' }}>{EASY_RETURN_45.name}</span>
                <span className="badge badge-yellow">Not wired up</span>
              </header>
              <p style={{ ...paragraph, fontWeight: 500, color: 'var(--color-primary)' }}>
                {EASY_RETURN_45.summary}
              </p>
              <p style={paragraph}>{EASY_RETURN_45.detail}</p>
              <p style={paragraph}>{EASY_RETURN_45.note}</p>
            </article>
          </Section>

          <Section icon={PackageOpen} title="Delivery and hand-over">
            <p style={{ ...paragraph, fontWeight: 600, color: 'var(--color-primary)' }}>
              Where the parcel ends up
            </p>
            <DefinitionList
              items={DELIVERY_TYPES.map((type) => ({
                key: type.value,
                term: type.name,
                description: type.detail,
              }))}
            />

            <p
              style={{
                ...paragraph,
                fontWeight: 600,
                color: 'var(--color-primary)',
                marginTop: '1rem',
              }}
            >
              How it reaches HP in the first place
            </p>
            <DefinitionList
              items={PICKUP_TYPES.map((type) => ({
                key: type.value,
                term: type.name,
                description: type.detail,
              }))}
            />
          </Section>

          <Section icon={Ruler} title="Parcel locker compartment sizes" last>
            <p style={paragraph}>
              Pick the smallest compartment your parcel fits into. The size is only required when a
              parcel locker is one end of the journey.
            </p>
            <ul style={{ ...bulletList, listStyle: 'none', paddingLeft: 0 }}>
              {PARCEL_SIZES.map((size) => (
                <li key={size.value} style={{ ...bulletItem, display: 'flex', gap: '0.6rem' }}>
                  <strong style={{ color: 'var(--color-primary)', minWidth: '2rem' }}>{size.value}</strong>
                  <span>{size.dims}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, last = false }) {
  return (
    <section
      style={{
        paddingBottom: last ? 0 : '1.25rem',
        marginBottom: last ? 0 : '1.25rem',
        borderBottom: last ? 'none' : '1px solid var(--color-border-subtle)',
      }}
    >
      <h4
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.95rem',
          fontWeight: 600,
          marginBottom: '0.6rem',
        }}
      >
        <Icon size={16} style={{ color: 'var(--color-brand-hp)', flexShrink: 0 }} />
        {title}
      </h4>
      {children}
    </section>
  );
}

function DefinitionList({ items }) {
  return (
    <dl style={{ margin: 0 }}>
      {items.map((item) => (
        <div key={item.key} style={{ marginBottom: '0.5rem' }}>
          <dt style={{ fontSize: '0.85rem', fontWeight: 600 }}>{item.term}</dt>
          <dd style={{ ...paragraph, margin: 0 }}>{item.description}</dd>
        </div>
      ))}
    </dl>
  );
}

const paragraph = {
  fontSize: '0.85rem',
  color: 'var(--color-secondary)',
  lineHeight: 1.55,
  marginBottom: '0.5rem',
};

const bulletList = {
  margin: '0 0 0.5rem',
  paddingLeft: '1.1rem',
};

const bulletItem = {
  fontSize: '0.85rem',
  color: 'var(--color-secondary)',
  lineHeight: 1.55,
  marginBottom: '0.3rem',
};

const serviceCard = {
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: '0.85rem 1rem',
  marginBottom: '0.75rem',
};

const serviceHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  flexWrap: 'wrap',
  marginBottom: '0.5rem',
};
